import 'reflect-metadata';
import {injectable} from 'inversify';
import {Omit} from 'react-redux';

import {HTTPMethods} from '../types/enums';
import {getApiUrl, isEnterpriseEnv} from '../utils/processHelpers';

import {CustomError} from './Errors';
import {requestLocal} from './LocalAPI';
import {AdminAPI, AdminTypes} from './api/Admin';
import {AuthAPI, AuthTypes} from './api/Auth';
import {MiscAPI, MiscTypes} from './api/Misc';
import {UserAPI, UserTypes} from './api/User';

export enum LocalStorageType {
    /**
     * API requests will always return/replace the whole storage key at once.
     */
    VALUE,
    /**
     * The entry within localStorage for this endpoint will be an array of items.
     *
     * Each item must have an `id` property, which is used to find and replace items within the array.
     */
    ARRAY,
    /**
     * The entry within localStorage for this endpoint will be a map of items.
     *
     * Each item must have an `id` property, which is used to key the map.
     */
    MAP
}

export interface LocalStorageOptions {
    type: LocalStorageType;

    /**
     * The name of the key used to persist values within localStorage.
     */
    key: string;

    /**
     * Optional function that is used to convert the values pulled from localStorage into the value that would be
     * returned by the server for that request.
     *
     * Can be used to apply any wrapping or evelope types that the server wraps around the returned resource.
     */
    transform?: APITransform<any>;

    /**
     * If reading a value from localStorage that doesn't (yet) exist, this value will be returned instead.
     *
     * If using `MAP` or `ARRAY` type, these types will automatically be initialised to an empty array/map. This
     * property is only needed to initialise to a non-empty collection, or to provide a fall-back when making a
     * GET request to an endpoint that has `VALUE` type and has not yet had a value written to it.
     */
    default?: any;
}

/**
 * Options that define an endpoint.
 *
 * These options are set during initialization and remain constant, they aren't configurable on a per-call basis.
 */
export interface APIEndpointOptions {
    /**
     * If set, localStorage will be used to provide basic offline (non-'enterprise') support for this endpoint.
     *
     * Basic CRUD functionality will be available, where values can be read/written/updated via fake HTTP requests.
     */
    storage?: LocalStorageOptions;

    /**
     * For any endpoints with `urlParams`,
     */
    defaultParams?: {};
}

/**
 * Outputs the second template argument, but only if the first argument is non-void (otherwise `{}`).
 */
type If<Cond, T extends {}> = Cond extends void ? {} : T;

/**
 * Options that can be passed at the point where an API call is made.
 *
 * The options available will depend on the call method, and if the URL has any parameters.
 */
export type APICallOptions<P, B> =
    Omit<RequestInit, 'body'> &
    If<P, {urlParams: P}> &
    If<B, {body: B}>;

type Options<S extends APIDefinition, M extends keyof S> = APICallOptions<SpecMethod<S, M, 1>, SpecMethod<S, M, 2>>;

// API Definition types & helpers
type Enum = string | number | symbol;
type APIMethodDefinition = [unknown, unknown, unknown];    // Response type, URL params, Body data type. Use void for any N/A args.
type APIDefinition = Partial<APIGetDefinition> & Partial<APIPostDefinition> & Partial<APIPutDefinition> & Partial<APIDeleteDefinition>;
type APIData<T> = [T, string, Partial<APIEndpointOptions>?];
interface APIGetDefinition {
    GET: [unknown, unknown];
}
interface APIPostDefinition {
    POST: APIMethodDefinition;
}
interface APIPutDefinition {
    PUT: APIMethodDefinition;
}
interface APIDeleteDefinition {
    DELETE: APIMethodDefinition;
}

/**
 * Defines a set of transforms for an endpoint. These are optional set of functions, that are used to simulate
 * inconsistencies in the server REST API when running in Enterprise mode.
 *
 * Each function should map the value stored in localStorage, to the value returned in the response for that
 * request. This typically means wrapping the given value in an envelope or other payload structure.
 */
export interface APITransform<S> {
    GET?: APIMethodTransform<S, HTTPMethods.GET>;
    POST?: APIMethodTransform<S, HTTPMethods.POST>;
    PUT?: APIMethodTransform<S, HTTPMethods.PUT>;
    DELETE?: APIMethodTransform<S, HTTPMethods.DELETE>;
}
/**
 * Defines the shape of a transform function.
 *
 * The function is passed the "raw" value, this will have the same type as the endpoint's `body` type (the third
 * element in the {@link APIMethodDefinition} tuple). The function should return a value that is of the expected
 * endpoint `response` type (the first element in the {@link APIMethodDefinition} tuple).
 */
type APIMethodTransform<S extends APIDefinition, M extends HTTPMethods> = (value: SpecMethod<S, M, 2>) => SpecMethod<S, M, 0>;

/**
 * One half of an API declaration - defines the data that is required at run-time to be able to create correct `fetch`
 * requests.
 */
export type APIDataSpec<T extends Record<string, any>> = {
    [key in keyof T]: APIData<T[key]>;
};
/**
 * Second half of an API declaration - defines the types of the request and response objects, plus any additional
 * options. These are used to validate calls to the methods of the `API` class.
 */
export type APIDefinitionSpec<T extends Enum> = {
    [key in T]: APIDefinition;
};
/**
 * Helper function to allow TypeScript to correctly infer types when creating `APIDataSpec` objects.
 *
 * @param endpoint The enum that identifies this endpoint
 * @param url URL of the server endpoint. May be absolute or relative - in the latter case, API will prepend a base URL.
 * @param options Any additional options for this endpoint, all are optional
 */
export function defineEndpoint<T extends Enum>(endpoint: T, url: string, options?: APIEndpointOptions): APIData<T> {
    return [endpoint, url, options];
}

/**
 * Given an endpoint definition and an HTTP request method, extracts a single field from that method's definition on
 * the endpoint.
 *
 * Will resolve to `never` if that method is not defined for the given endpoint.
 */
type SpecMethod<S extends APIDefinition, M extends keyof S, I extends keyof APIMethodDefinition> = I extends keyof S[M] ? S[M][I] : never;

// TODO: [SERVICE-1145] This object is parsed by `ExceptionMessage`
// Server response when there is an error with data send by the client e.g. "Invalid phone number"
export interface ResponseError {
    status: string; // Should be an enum
    code: string; // Should be an enum
    message: string;
}

/**
 * Error type for any HTTP/fetch errors, includes details of the request that resulted in the error.
 */
export class APIError extends CustomError {
    constructor(
        message: string,
        url: string,
        status: number,
        options: RequestInit,
        innerError?: Error,
        metadata?: {[key: string]: any}
    ) {
        super('APIError', message, innerError, {...metadata, url, status, options});
    }

    public get url(): string {
        return this.metadata!.url;
    }

    public get status(): number {
        return this.metadata!.status;
    }

    public get options(): RequestInit {
        return this.metadata!.options;
    }

    public get response(): ResponseError | undefined {
        if (this.metadata) {
            return this.metadata!.response;
        }
        return undefined;
    }
}

const STATUS_ERROR_MESSAGE = {
    413: 'Payload too large.'
};
const trailingSlash: RegExp = /\/$/;

@injectable()
/**
 * Util for managing HTTP requests to a remote REST API.
 *
 * Provides type safety for all calls to the API, based on an API definition that consists of a combination of
 * TypeScript types and static data that is used at runtime to build fetch requests.
 *
 * Each endpoint definition consists of three parts:
 * - An enum that lists the available endpoints
 * - A type definition that maps each enum to the expected request, response, and URL params for each HTTP method
 * - A constant that provides the data required to build the request (eg: endpoint URL)
 *
 * By convention, these endpoint definitions are split into multiple files, following the same structure as the
 * endpoints themselves.
 *
 * As an example, this would define an endpoint for the `api/user/layouts` resource, which has GET, POST and DELETE
 * methods - for fetching, creating and deleting layouts respectively.
 *
 * ```
 * export enum UserAPI {
 *     // Defines the "layouts" server endpoint
 *     LAYOUTS = 'USER_LAYOUTS',
 * }
 *
 * export const UserData: APIDataSpec<typeof UserAPI> = {
 *     // The endpoint URL (can be relative or absolute - in this case, `API` will prepend the base URL)
 *     LAYOUTS: defineEndpoint(UserAPI.LAYOUTS, 'api/user/layouts', {storageKey: LOCAL_STORAGE_KEYS.LAYOUTS})
 * };
 * export interface UserTypes extends APIDefinitionSpec<UserAPI> {
 *     // This endpoint supports the 'GET', 'POST' and 'DELETE' methods
 *     [UserAPI.LAYOUTS]: {
 *         // The 'GET' method returns `UserLayout[]`, and takes no additional URL args
 *         GET: [UserLayout[], void],
 *         // The 'POST' method returns void, takes no additional URL args, and expects a `NewUserLayout` object as the body
 *         POST: [{status: string, layout: UserLayout}, void, NewUserLayout],
 *         // The 'DELETE' method takes no body or return value, and expects to be given an 'id' parameter when called, which will be used to build the URL
 *         DELETE: [void, {id: string}, void]
 *     }
 * }
 * ```
 *
 * Controllers can then make calls to the above endpoints as follows:
 * ```
 * // Fetch a list of all existing layouts
 * // This will send a GET request to https://<base URL>/api/user/layouts
 * const workspaces: UserLayout[] = await this._api.get(UserData.LAYOUTS);
 *
 * // Create a new layout
 * // This will send a POST request to https://<base URL>/api/user/layouts, with a body of newLayout and returning a "wrapped" layout object
 * const newLayout: NewUserLayout = { ... };
 * const result = this._api.post(UserData.LAYOUTS, {body: newLayout});
 * console.log(`Status: ${result.status}, layout: `, result.layout);
 *
 * // Delete an existing layout
 * // This will send a DELETE request to https://<base URL>/api/user/layouts/1234567890
 * const layoutToRemove: string = '1234567890'; // some layout id
 * this._api.delete(UserData.LAYOUTS, {urlParams: {id: layoutToRemove}});
 * ```
 *
 * The template arguments of this util provide the list of endpoints, and the expected type defintions of each endpoint
 * and method. Using the example above, the util should be instantiated as `new _API<UserAPI, UserTypes>()`. If
 * combining multiple endpoint definitions, take the *union* of endpoint enums and the *intersection* of type definitions.
 */
class _API<T extends Enum, S extends APIDefinitionSpec<T>> {    // eslint-disable-line @typescript-eslint/class-name-casing
    public async get<K extends T>(config: APIData<K>, options?: APICallOptions<SpecMethod<S[K], 'GET', 1>, void>): Promise<SpecMethod<S[K], 'GET', 0>> {
        return this.request(config, HTTPMethods.GET, options && options.urlParams, undefined);
    }

    public async post<K extends T>(config: APIData<K>, options: Options<S[K], 'POST'>): Promise<SpecMethod<S[K], 'POST', 0>> {
        return this.request(config, HTTPMethods.POST, options.urlParams, options.body);
    }

    public async put<K extends T>(config: APIData<K>, options: Options<S[K], 'PUT'>): Promise<SpecMethod<S[K], 'PUT', 0>> {
        return this.request(config, HTTPMethods.PUT, options.urlParams, options.body);
    }

    public async delete<K extends T>(config: APIData<K>, options: Options<S[K], 'DELETE'>): Promise<SpecMethod<S[K], 'DELETE', 0>> {
        return this.request(config, HTTPMethods.DELETE, options.urlParams, options.body);
    }

    public async request<K extends T, M extends keyof typeof HTTPMethods>(
        config: APIData<K>,
        method: M,
        urlParams?: SpecMethod<S[K], M, 1>,
        body?: SpecMethod<S[K], M, 2>
    ): Promise<SpecMethod<S[K], M, 0>> {
        const [, urlTemplate, options = {}] = config;

        if (isEnterpriseEnv()) {
            const url: string = this.replaceParams(urlTemplate, urlParams as {}, options.defaultParams);
            const serializedBody: string | null = body ? (typeof body !== 'string' ? JSON.stringify(body) : body) : null;
            const request: RequestInit = {
                body: serializedBody,
                credentials: 'include',
                headers: {
                    'content-type': 'application/json'
                },
                method,
                mode: 'cors'
            };
            const result: SpecMethod<S[K], M, 0> = await this.fetch(url, request);

            return result;
        } else if (options.storage) {
            // Use localStorage as a simple CRUD data store
            return requestLocal(options.storage, method as HTTPMethods, urlParams as {}, body as {}) as SpecMethod<S[K], M, 0>;
        } else {
            throw new Error();
        }
    }

    private async fetch<Data>(url: string, options: RequestInit): Promise<Data> {
        let response: Response;
        try {
            response = await fetch(url, options);
        } catch (e) {
            const error: Error = e.error || e;
            const message = `${options.method || 'GET'} ${url}: Transport error (${error.message || error})`;
            throw new APIError(message, url, e.status || 0, options, error);
        }

        if (response.status >= 400) {
            // If user is not authenticated (indicated by 'UNAUTHORIZED' response), then log the user out
            if (response.status === 401) {
                // TODO: [SERVICE-889] Log the user out if a 401 response is received (in a way that doesn't create race conditions...)
                // await this.store.dispatch(new Authenticated(false));
            }

            // Fetch and error info from the response
            const resultPromise = (response.headers.get('Content-Type') || '').startsWith('application/json') ? response.json() : response.text();
            const result = await resultPromise.catch((e) => {
                console.warn('Exception when reading error response:', e);
                const errorMessage = STATUS_ERROR_MESSAGE[response.status];
                return errorMessage ? new Error(errorMessage) : e;
            });

            // Raise error
            const message = `${options.method || 'GET'} ${url}: Server returned ${response.status} (${response.statusText})`;
            throw new APIError(message, url, response.status || 0, options, undefined, {response: result});
        }

        try {
            return response.json();
        } catch (e) {
            const error: Error = e.error || e;
            const contentType: string | null = response.headers.get('Content-Type');
            const message = `${options.method || 'GET'} ${url}: Parse error (Content-Type: ${contentType}. Message: ${error.message || error})`;
            throw new APIError(message, url, e.status || response.status || 0, options, error);
        }
    }

    private replaceParams(template: string, callParams: {} | undefined, defaultParams?: {}) {
        let url: string = template;

        // If URL looks like a string template, insert params into string
        if (template.indexOf('$') >= 0) {
            const params = {...defaultParams, ...callParams};
            let templateFunc: Function;

            try {
                // eslint-disable-next-line no-new-func
                templateFunc = new Function(...Object.keys(params), `return \`${template}\`;`);
            } catch (e) {
                console.error('Error creating template function, check for syntax errors and any characters that need escaping within template string');
                throw e;
            }

            url = templateFunc(...Object.values(params)).replace(trailingSlash, '');
        }

        // Prepend base URL for any non-absolute URLs
        if (!url.includes('://')) {
            url = `${getApiUrl()}${url}`;
        }

        return url;
    }
}

type LauncherAPI = AdminAPI | AuthAPI | MiscAPI | UserAPI;
type LauncherSpec = AdminTypes & AuthTypes & MiscTypes & UserTypes;

export class API extends _API<LauncherAPI, LauncherSpec> {
}
